import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { CircularProgress, Box, ThemeProvider } from '@mui/material';
import theme from './theme/engineeringTheme';
import Header from "./components/Header";
import PrivateRoute from './components/PrivateRoute';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import ReactGA from 'react-ga4';
import './utils/axiosConfig'; 

// Lazy load all pages for better performance
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const CanvasEditor = lazy(() => import("./components/editor/CanvasEditor"));
const ViewerPage = lazy(() => import("./pages/ViewerPage"));
const Account = lazy(() => import("./pages/Account"));
const Pricing = lazy(() => import("./pages/Pricing"));
//const Payment = lazy(() => import("./pages/Payment"));
const Return = lazy(() => import("./pages/Return"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const ForgotPassword = lazy(() => import("./pages/Forgot-Password"));
const ResetPassword = lazy(() => import("./pages/Reset-Password"));
const VerifyEmail = lazy(() => import("./pages/Verify-Email"));
const Error = lazy(() => import("./pages/Error"));
//const BowtieTablePage = lazy(() => import("./pages/Bowtie-Table"));
const EmailVerificationPending = lazy(() => import("./pages/Email-Verification-Pending"));
const EmailVerificationStatus = lazy(() => import("./pages/Email-Verification-Status"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Organisation = lazy(() => import("./pages/Organisation"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const ConnectYourAiAssistant = lazy(() => import("./pages/ConnectYourAiAssistant"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);
  return null;
}

// Loading component using MUI - perfectly centered on full viewport
const LoadingComponent = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    height="100vh"
    flexDirection="column"
    position="fixed"
    top={0}
    left={0}
    width="100vw"
    zIndex={9999}
  >
    <CircularProgress />
    <Box mt={2}>Loading...</Box>
  </Box>
);

function App() {
  useEffect(() => {
    if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
      ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <RouteTracker />
        <div className="container">
          <Header />
          <Suspense fallback={<LoadingComponent />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/connect-your-ai-assistant" element={<ConnectYourAiAssistant />} />
              <Route path="/api-docs" element={<ApiDocs />} />
              <Route path="/viewer/:bowtie" element={<ViewerPage />} />
              <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
              <Route path="/email-verification-status" element={<EmailVerificationStatus />} />
              
              {/* Authentication Routes */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:id/:token" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              
              {/* Payment Routes */}
              <Route path="/subscribe" element={<Subscribe />} />
              {/* <Route path="/payment" element={<Payment />} /> */}
              <Route path="/return" element={<Return />} />
              
              {/* Private Routes */}
              <Route path="/editor/:bowtie" element={<PrivateRoute requireSubscription={true}><CanvasEditor /></PrivateRoute>} />
              {/* <Route path="/bowtie-table/:bowtie" element={<PrivateRoute requireSubscription={true}><BowtieTablePage /></PrivateRoute>} /> */}
              <Route path="/account" element={<PrivateRoute><Account /></PrivateRoute>} />

              <Route path="/dashboard" element={<PrivateRoute requireSubscription={true}><Dashboard /></PrivateRoute>} />

              {/* Organisations */}
              <Route path="/invite/accept" element={<AcceptInvite />} />
              <Route path="/orgs/:slug" element={<PrivateRoute><Organisation /></PrivateRoute>} />
              
              {/* Error Route - must be last */}
              <Route path="*" element={<Error />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
      <ToastContainer />
    </ThemeProvider>
  );
}

export default App;
